import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import {
    Autocomplete,
    Button,
    ButtonBase,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Grid,
    IconButton,
    InputAdornment,
    MenuItem,
    Switch,
    TextField,
    Tooltip,
} from "@mui/material";
import CampaignOutlinedIcon from "@mui/icons-material/CampaignOutlined";
import CheckIcon from "@mui/icons-material/Check";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import EventAvailableOutlinedIcon from "@mui/icons-material/EventAvailableOutlined";
import GroupOutlinedIcon from "@mui/icons-material/GroupOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import NotificationsNoneOutlinedIcon from "@mui/icons-material/NotificationsNoneOutlined";
import ScheduleOutlinedIcon from "@mui/icons-material/ScheduleOutlined";
import StorefrontOutlinedIcon from "@mui/icons-material/StorefrontOutlined";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import { TYPE_DESCRIPTIONS, UI } from "./format";

/*
 * Notification rules of a file (or a customer's default profile). One row per
 * notification type: who gets it, through which channel, and when. The table
 * shows a summary of every rule; the dialog edits one rule in full.
 */

const SCHEDULE_FIELDS = {
    ETA_POD: ["beforeEtaHours"],
    ETA_TRANSIT: [],
    OVERDUE_ARRIVAL: ["toleranceHours", "repeatDaily", "sendHour"],
    NOT_PICKED_UP: ["afterHours", "repeatDaily", "sendHour"],
    FREE_TIME_WARNING: ["beforeDays", "sendHour"],
    FREE_TIME_EXPIRED: ["repeatDaily", "sendHour"],
    // Air / road files
    OVERDUE_DEPARTURE: ["toleranceHours", "repeatDaily", "sendHour"],
    LATE_DELIVERY: ["repeatDaily", "sendHour"],
    POD_MISSING: ["afterHours", "repeatDaily", "sendHour"],
};

const GROUPS = [
    { category: "EVENT", title: "Sefer olayları", subtitle: "Gerçekleşen her olayda bir kez gönderilir." },
    { category: "EXPECTATION", title: "Beklenti uyarıları", subtitle: "Beklenen bir gelişme olmadığında ya da süre daraldığında gönderilir." },
];

// Columns follow the panel's own width (it sits in a drawer), not the window's.
const WIDE = "@container (min-width: 820px)";
const ROW_GRID = {
    display: "grid",
    gridTemplateColumns: "44px 1fr 40px",
    [WIDE]: { gridTemplateColumns: "44px minmax(0, 2.2fr) minmax(0, 1.2fr) 76px minmax(0, 0.9fr) minmax(0, 1.3fr) 40px" },
};
const DESKTOP_ONLY = { display: "none", [WIDE]: { display: "block" } };

const splitList = (text) =>
    String(text || "")
        .split(/[,;\s]+/)
        .map((s) => s.trim())
        .filter(Boolean);

const hour = (h) => `${String(h ?? 9).padStart(2, "0")}:00`;

const scheduleSummary = (row) => {
    const s = row.schedule || {};
    const repeat = s.repeatDaily !== false ? `, sonra her gün ${hour(s.sendHour)}` : "";
    switch (row.type) {
        case "ETA_POD":
            return s.beforeEtaHours > 0 ? `ETA bilgisinde + ETA'dan ${s.beforeEtaHours} sa önce` : "ETA bilgisi geldiğinde";
        case "ETA_CHANGE":
            return row.etaThresholdHours > 0 ? `ETA ${row.etaThresholdHours} sa ve üzeri değişince` : "Her ETA değişikliğinde";
        case "OVERDUE_ARRIVAL":
            return `ETA + ${s.toleranceHours ?? 6} sa${repeat}`;
        case "NOT_PICKED_UP":
            return `Tahliye + ${s.afterHours ?? 24} sa${repeat}`;
        case "FREE_TIME_WARNING":
            return `${(s.beforeDays || [3, 2, 1]).join(" / ")} gün kala, ${hour(s.sendHour)}`;
        case "OVERDUE_DEPARTURE":
            return `ETD + ${s.toleranceHours ?? 6} sa${repeat}`;
        case "LATE_DELIVERY":
            return `RDL günü bitince${repeat}`;
        case "POD_MISSING":
            return `Teslim + ${s.afterHours ?? 24} sa${repeat}`;
        case "FREE_TIME_EXPIRED":
            return s.repeatDaily !== false ? `Hemen, sonra her gün ${hour(s.sendHour)}` : "Bir kez";
        default:
            return "Olay anında";
    }
};

const internalNames = (row, meId, userById) => {
    const ids = (row.internalUsers || []).map(String);
    const names = [];
    if (meId && ids.includes(String(meId))) names.push("Ben");
    if (row.includeSalesRep !== false) names.push("Satış temsilcisi");
    ids.filter((id) => id !== String(meId)).forEach((id) => names.push(userById.get(id)?.label || "Kullanıcı"));
    (row.extraEmails || []).forEach((email) => names.push(email));
    return names;
};

const Pill = ({ tone, children }) => (
    <MDBox
        component="span"
        sx={{
            display: "inline-flex",
            alignItems: "center",
            px: 1,
            py: 0.25,
            borderRadius: "6px",
            fontSize: 12,
            fontWeight: 500,
            lineHeight: 1.6,
            whiteSpace: "nowrap",
            color: UI.tone[tone].fg,
            backgroundColor: UI.tone[tone].bg,
        }}
    >
        {children}
    </MDBox>
);

Pill.propTypes = { tone: PropTypes.oneOf(["neutral", "info", "success", "warning", "danger"]).isRequired, children: PropTypes.node.isRequired };

const ChannelToggle = ({ active, icon, label, disabled, onClick }) => (
    <Tooltip title={`${label}: ${active ? "açık" : "kapalı"}`}>
        <span>
            <IconButton
                size="small"
                disabled={disabled}
                onClick={onClick}
                aria-label={label}
                sx={{
                    width: 30,
                    height: 30,
                    borderRadius: "8px",
                    border: `1px solid ${active ? UI.tone.info.border : UI.border}`,
                    backgroundColor: active ? UI.tone.info.bg : "transparent",
                    color: active ? UI.tone.info.fg : UI.muted,
                }}
            >
                {icon}
            </IconButton>
        </span>
    </Tooltip>
);

ChannelToggle.defaultProps = { disabled: false };
ChannelToggle.propTypes = {
    active: PropTypes.bool.isRequired,
    icon: PropTypes.node.isRequired,
    label: PropTypes.string.isRequired,
    disabled: PropTypes.bool,
    onClick: PropTypes.func.isRequired,
};

const SectionLabel = ({ children }) => (
    <MDTypography variant="caption" fontWeight="bold" display="block" mb={1} sx={{ color: UI.text, letterSpacing: 0.2 }}>
        {children}
    </MDTypography>
);

SectionLabel.propTypes = { children: PropTypes.node.isRequired };

// A titled block of the rule dialog.
const Section = ({ icon, title, subtitle, action, children }) => (
    <MDBox sx={{ p: 2, borderRadius: "12px", border: `1px solid ${UI.border}`, backgroundColor: "#fff" }}>
        <MDBox display="flex" alignItems="center" gap={1.25} mb={1.75}>
            <MDBox
                sx={{
                    width: 30,
                    height: 30,
                    flexShrink: 0,
                    borderRadius: "8px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: UI.text,
                    backgroundColor: UI.subtle,
                    border: `1px solid ${UI.border}`,
                    "& svg": { fontSize: "18px !important" },
                }}
            >
                {icon}
            </MDBox>
            <MDBox flex={1} minWidth={0}>
                <MDTypography variant="button" fontWeight="bold" display="block" sx={{ color: UI.text, lineHeight: 1.3 }}>
                    {title}
                </MDTypography>
                {subtitle && (
                    <MDTypography variant="caption" display="block" sx={{ color: UI.muted, lineHeight: 1.3 }}>
                        {subtitle}
                    </MDTypography>
                )}
            </MDBox>
            {action}
        </MDBox>
        {children}
    </MDBox>
);

Section.defaultProps = { subtitle: "", action: null };
Section.propTypes = {
    icon: PropTypes.node.isRequired,
    title: PropTypes.string.isRequired,
    subtitle: PropTypes.string,
    action: PropTypes.node,
    children: PropTypes.node.isRequired,
};

// A selectable tile: checkbox (or radio) with a label and a short hint.
const OptionTile = ({ checked, disabled, radio, icon, label, hint, onChange }) => (
    <ButtonBase
        disabled={disabled}
        onClick={() => onChange(radio ? true : !checked)}
        role={radio ? "radio" : "checkbox"}
        aria-checked={checked}
        sx={{
            width: "100%",
            height: "100%",
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "flex-start",
            gap: 1,
            p: 1.25,
            textAlign: "left",
            borderRadius: "10px",
            border: `1px solid ${checked ? UI.tone.info.fg : UI.border}`,
            backgroundColor: checked ? UI.tone.info.bg : "#fff",
            opacity: disabled ? 0.5 : 1,
            transition: "border-color .15s, background-color .15s",
            "&:hover": { borderColor: checked ? UI.tone.info.fg : "#94a3b8" },
        }}
    >
        <MDBox
            sx={{
                width: 18,
                height: 18,
                mt: 0.125,
                flexShrink: 0,
                borderRadius: radio ? "50%" : "5px",
                border: `2px solid ${checked ? UI.tone.info.fg : "#cbd5e1"}`,
                backgroundColor: checked && !radio ? UI.tone.info.fg : "#fff",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
            }}
        >
            {checked && radio && <MDBox sx={{ width: 8, height: 8, borderRadius: "50%", backgroundColor: UI.tone.info.fg }} />}
            {checked && !radio && <CheckIcon sx={{ fontSize: "13px !important", color: "#fff" }} />}
        </MDBox>
        <MDBox minWidth={0}>
            <MDBox display="flex" alignItems="center" gap={0.5} sx={{ color: checked ? UI.tone.info.fg : UI.text }}>
                {icon}
                <MDTypography variant="button" fontWeight="medium" sx={{ color: "inherit", lineHeight: 1.3 }}>
                    {label}
                </MDTypography>
            </MDBox>
            {hint && (
                <MDTypography variant="caption" display="block" sx={{ color: UI.muted, lineHeight: 1.35, mt: 0.25 }}>
                    {hint}
                </MDTypography>
            )}
        </MDBox>
    </ButtonBase>
);

OptionTile.defaultProps = { disabled: false, radio: false, icon: null, hint: "" };
OptionTile.propTypes = {
    checked: PropTypes.bool.isRequired,
    disabled: PropTypes.bool,
    radio: PropTypes.bool,
    icon: PropTypes.node,
    label: PropTypes.string.isRequired,
    hint: PropTypes.string,
    onChange: PropTypes.func.isRequired,
};


function RuleDialog({ row, meta, users, meId, customerContacts, onClose, onApply }) {
    const [draft, setDraft] = useState(null);

    useEffect(() => {
        if (!row) return;
        setDraft({
            ...row,
            schedule: { ...(row.schedule || {}) },
            channels: { ...(row.channels || {}) },
            extraEmailsText: (row.extraEmails || []).join(", "),
            beforeDaysText: (row.schedule?.beforeDays || [3, 2, 1]).join(", "),
        });
    }, [row]);

    if (!row || !draft) return null;

    const set = (patch) => setDraft((d) => ({ ...d, ...patch }));
    const setSchedule = (patch) => setDraft((d) => ({ ...d, schedule: { ...d.schedule, ...patch } }));
    const userById = new Map(users.map((u) => [String(u.id), u]));
    const internalIds = (draft.internalUsers || []).map(String);
    const isMine = meId && internalIds.includes(String(meId));
    const fields = SCHEDULE_FIELDS[row.type] || [];
    const hasSchedule = fields.length > 0 || row.type === "ETA_CHANGE";

    const apply = () => {
        const { extraEmailsText, beforeDaysText, ...rest } = draft;
        const result = { ...rest, extraEmails: splitList(extraEmailsText) };
        if (fields.includes("beforeDays")) {
            result.schedule = {
                ...result.schedule,
                beforeDays: splitList(beforeDaysText)
                    .map(Number)
                    .filter((n) => Number.isInteger(n) && n >= 0),
            };
        }
        onApply(result);
    };

    const enabled = draft.enabled !== false;
    const expectation = meta.category === "EXPECTATION";
    const headTone = expectation ? "warning" : "info";
    const HeadIcon = expectation ? WarningAmberRoundedIcon : EventAvailableOutlinedIcon;
    const toggleMine = (checked) =>
        set({ internalUsers: checked ? [...internalIds, String(meId)] : internalIds.filter((id) => id !== String(meId)) });

    // One line of what the rule will do, as it stands in the dialog.
    const recipients = internalNames({ ...draft, extraEmails: splitList(draft.extraEmailsText) }, meId, userById);
    const channelNames = [draft.channels.inApp !== false && "sistem içi", draft.channels.email !== false && "e-posta"].filter(Boolean);
    const summary = enabled
        ? [
              recipients.length ? `${recipients.length} iç alıcı` : "İç alıcı yok",
              channelNames.length ? channelNames.join(" + ") : "kanal seçilmedi",
              !meta.internalOnly && draft.notifyCustomer ? `müşteriye ${draft.customerMode === "AUTO" ? "otomatik" : "onayla"}` : null,
              scheduleSummary(draft).toLocaleLowerCase("tr-TR"),
          ]
              .filter(Boolean)
              .join(" · ")
        : "Kural kapalı; bu dosya için bildirim oluşmaz.";

    return (
        <Dialog open onClose={onClose} maxWidth="lg" fullWidth PaperProps={{ sx: { maxWidth: 1040, borderRadius: "16px", overflow: "hidden" } }}>
            <MDBox sx={{ height: 4, backgroundColor: enabled ? UI.tone[headTone].fg : UI.border }} />
            <DialogTitle sx={{ px: 3, pt: 2.5, pb: 2, borderBottom: `1px solid ${UI.border}` }}>
                <MDBox display="flex" alignItems="flex-start" justifyContent="space-between" gap={2}>
                    <MDBox display="flex" alignItems="flex-start" gap={1.5} minWidth={0}>
                        <MDBox
                            sx={{
                                width: 44,
                                height: 44,
                                flexShrink: 0,
                                borderRadius: "12px",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                color: UI.tone[headTone].fg,
                                backgroundColor: UI.tone[headTone].bg,
                                border: `1px solid ${UI.tone[headTone].border}`,
                            }}
                        >
                            <HeadIcon />
                        </MDBox>
                        <MDBox minWidth={0}>
                            <MDBox display="flex" alignItems="center" gap={1} flexWrap="wrap">
                                <MDTypography variant="h6" sx={{ color: UI.text, lineHeight: 1.3 }}>
                                    {meta.label}
                                </MDTypography>
                                <Pill tone={headTone}>{expectation ? "Beklenti uyarısı" : "Sefer olayı"}</Pill>
                                {meta.internalOnly && <Pill tone="neutral">Yalnızca iç</Pill>}
                            </MDBox>
                            <MDTypography variant="caption" display="block" sx={{ color: UI.muted, mt: 0.5, lineHeight: 1.5 }}>
                                {meta.description || TYPE_DESCRIPTIONS[row.type]}
                            </MDTypography>
                        </MDBox>
                    </MDBox>
                    <MDBox
                        sx={{
                            flexShrink: 0,
                            display: "flex",
                            alignItems: "center",
                            gap: 0.5,
                            pl: 1.5,
                            pr: 0.5,
                            borderRadius: "999px",
                            border: `1px solid ${enabled ? UI.tone.success.border : UI.border}`,
                            backgroundColor: enabled ? UI.tone.success.bg : UI.subtle,
                        }}
                    >
                        <MDTypography variant="caption" fontWeight="bold" sx={{ color: enabled ? UI.tone.success.fg : UI.muted }}>
                            {enabled ? "Aktif" : "Kapalı"}
                        </MDTypography>
                        <Switch size="small" checked={enabled} onChange={(e) => set({ enabled: e.target.checked })} />
                    </MDBox>
                </MDBox>
            </DialogTitle>

            <DialogContent sx={{ px: 3, pb: 3, backgroundColor: UI.subtle }}>
                <MDBox
                    display="grid"
                    gridTemplateColumns={{ xs: "1fr", md: "1fr 1fr" }}
                    gap={2}
                    pt={2.5}
                    sx={{ opacity: enabled ? 1 : 0.55, transition: "opacity .15s" }}
                >
                    <MDBox display="flex" flexDirection="column" gap={2}>
                        <Section icon={<GroupOutlinedIcon />} title="Kime gider" subtitle="Filenders içinde bildirimi alacak kişiler">
                            <MDBox display="grid" gridTemplateColumns="1fr 1fr" gap={1}>
                                <OptionTile checked={!!isMine} disabled={!meId} label="Bana gönder" hint="Oturum açan kullanıcı" onChange={toggleMine} />
                                <OptionTile
                                    checked={draft.includeSalesRep !== false}
                                    label="Satış temsilcisi"
                                    hint="Dosyadaki Sales Rep"
                                    onChange={(checked) => set({ includeSalesRep: checked })}
                                />
                            </MDBox>
                            <Autocomplete
                                multiple
                                size="small"
                                options={users.map((u) => String(u.id))}
                                getOptionLabel={(id) => userById.get(id)?.label || id}
                                value={internalIds.filter((id) => id !== String(meId))}
                                onChange={(_e, value) => set({ internalUsers: [...value, ...(isMine ? [String(meId)] : [])] })}
                                renderInput={(params) => <TextField {...params} label="Diğer kullanıcılar" placeholder="Operasyon, satış…" />}
                                sx={{ mt: 2 }}
                            />
                            <TextField
                                fullWidth
                                size="small"
                                label="Ek e-posta adresleri"
                                placeholder="ornek@filenders.com, …"
                                helperText="Birden fazla adresi virgülle ayırın"
                                value={draft.extraEmailsText}
                                onChange={(e) => set({ extraEmailsText: e.target.value })}
                                sx={{ mt: 2 }}
                            />
                        </Section>

                        <Section icon={<CampaignOutlinedIcon />} title="Kanal" subtitle="Bildirimin iç alıcılara nasıl ulaşacağı">
                            <MDBox display="grid" gridTemplateColumns="1fr 1fr" gap={1}>
                                <OptionTile
                                    checked={draft.channels.inApp !== false}
                                    icon={<NotificationsNoneOutlinedIcon fontSize="small" />}
                                    label="Sistem içi"
                                    hint="Zil menüsünde görünür"
                                    onChange={(checked) => set({ channels: { ...draft.channels, inApp: checked } })}
                                />
                                <OptionTile
                                    checked={draft.channels.email !== false}
                                    icon={<MailOutlineIcon fontSize="small" />}
                                    label="E-posta"
                                    hint="Filentech şablonuyla"
                                    onChange={(checked) => set({ channels: { ...draft.channels, email: checked } })}
                                />
                            </MDBox>
                        </Section>
                    </MDBox>

                    <MDBox display="flex" flexDirection="column" gap={2}>
                        <Section
                            icon={<StorefrontOutlinedIcon />}
                            title="Müşteri"
                            subtitle="Müşteri yetkililerine e-posta"
                            action={
                                meta.internalOnly ? null : (
                                    <Switch size="small" checked={!!draft.notifyCustomer} onChange={(e) => set({ notifyCustomer: e.target.checked })} />
                                )
                            }
                        >
                            {meta.internalOnly && (
                                <MDBox
                                    display="flex"
                                    alignItems="center"
                                    gap={1}
                                    sx={{ p: 1.5, borderRadius: "10px", backgroundColor: UI.subtle, border: `1px dashed ${UI.border}` }}
                                >
                                    <LockOutlinedIcon sx={{ color: UI.muted, fontSize: "18px !important" }} />
                                    <MDTypography variant="caption" sx={{ color: UI.muted }}>
                                        Bu uyarı operasyon içindir; müşteriye gönderilmez.
                                    </MDTypography>
                                </MDBox>
                            )}
                            {!meta.internalOnly && !draft.notifyCustomer && (
                                <MDTypography variant="caption" display="block" sx={{ color: UI.muted, lineHeight: 1.6 }}>
                                    Kapalı. Açtığınızda seçtiğiniz yetkililere, operatör onayından sonra ya da otomatik olarak e-posta gider.
                                </MDTypography>
                            )}
                            {!meta.internalOnly && draft.notifyCustomer && (
                                <>
                                    <Autocomplete
                                        multiple
                                        freeSolo
                                        size="small"
                                        options={customerContacts.map((c) => c.email)}
                                        getOptionLabel={(email) => {
                                            const c = customerContacts.find((x) => x.email === email);
                                            return c ? `${c.name || email} (${email})` : email;
                                        }}
                                        value={draft.customerEmails || []}
                                        onChange={(_e, value) => set({ customerEmails: value.map((v) => String(v).trim().toLowerCase()) })}
                                        renderInput={(params) => <TextField {...params} label="Müşteri yetkilileri" placeholder="Seçin veya e-posta yazın" />}
                                    />
                                    <MDTypography variant="caption" fontWeight="bold" display="block" sx={{ color: UI.muted, mt: 2, mb: 0.75 }}>
                                        Gönderim şekli
                                    </MDTypography>
                                    <MDBox display="grid" gridTemplateColumns="1fr 1fr" gap={1}>
                                        <OptionTile
                                            radio
                                            checked={(draft.customerMode || "APPROVAL") === "APPROVAL"}
                                            label="Onaylı"
                                            hint="Operatör onaylayınca gider"
                                            onChange={() => set({ customerMode: "APPROVAL" })}
                                        />
                                        <OptionTile
                                            radio
                                            checked={draft.customerMode === "AUTO"}
                                            label="Otomatik"
                                            hint="Müşteri profilinde izin gerekir"
                                            onChange={() => set({ customerMode: "AUTO" })}
                                        />
                                    </MDBox>
                                </>
                            )}
                        </Section>

                        {hasSchedule && (
                            <Section icon={<ScheduleOutlinedIcon />} title="Zamanlama" subtitle="Bildirimin ne zaman ve ne sıklıkta gideceği">
                                <Grid container spacing={1.5}>
                                    {row.type === "ETA_CHANGE" && (
                                        <Grid item xs={12} sm={6}>
                                            <TextField
                                                fullWidth
                                                size="small"
                                                type="number"
                                                label="ETA farkı eşiği"
                                                helperText="0 = her değişiklikte"
                                                InputProps={{ endAdornment: <InputAdornment position="end">saat</InputAdornment> }}
                                                value={draft.etaThresholdHours ?? 12}
                                                onChange={(e) => set({ etaThresholdHours: Number(e.target.value) })}
                                            />
                                        </Grid>
                                    )}
                                    {fields.includes("beforeEtaHours") && (
                                        <Grid item xs={12} sm={6}>
                                            <TextField
                                                fullWidth
                                                size="small"
                                                type="number"
                                                label="ETA'dan önce hatırlat"
                                                helperText="0 = hatırlatma yok"
                                                InputProps={{ endAdornment: <InputAdornment position="end">saat</InputAdornment> }}
                                                value={draft.schedule.beforeEtaHours ?? 0}
                                                onChange={(e) => setSchedule({ beforeEtaHours: Number(e.target.value) })}
                                            />
                                        </Grid>
                                    )}
                                    {fields.includes("toleranceHours") && (
                                        <Grid item xs={12} sm={6}>
                                            <TextField
                                                fullWidth
                                                size="small"
                                                type="number"
                                                label={row.type === "OVERDUE_DEPARTURE" ? "ETD'den sonra" : "ETA'dan sonra"}
                                                InputProps={{ endAdornment: <InputAdornment position="end">saat</InputAdornment> }}
                                                value={draft.schedule.toleranceHours ?? 6}
                                                onChange={(e) => setSchedule({ toleranceHours: Number(e.target.value) })}
                                            />
                                        </Grid>
                                    )}
                                    {fields.includes("afterHours") && (
                                        <Grid item xs={12} sm={6}>
                                            <TextField
                                                fullWidth
                                                size="small"
                                                type="number"
                                                label={row.type === "POD_MISSING" ? "Teslimden sonra" : "Tahliyeden sonra"}
                                                InputProps={{ endAdornment: <InputAdornment position="end">saat</InputAdornment> }}
                                                value={draft.schedule.afterHours ?? 24}
                                                onChange={(e) => setSchedule({ afterHours: Number(e.target.value) })}
                                            />
                                        </Grid>
                                    )}
                                    {fields.includes("beforeDays") && (
                                        <Grid item xs={12} sm={6}>
                                            <TextField
                                                fullWidth
                                                size="small"
                                                label="Kaç gün kala"
                                                helperText="Örn. 3, 2, 1"
                                                value={draft.beforeDaysText}
                                                onChange={(e) => set({ beforeDaysText: e.target.value })}
                                            />
                                        </Grid>
                                    )}
                                    {fields.includes("sendHour") && (
                                        <Grid item xs={12} sm={6}>
                                            <TextField
                                                select
                                                fullWidth
                                                size="small"
                                                label="Hatırlatma saati"
                                                value={draft.schedule.sendHour ?? 9}
                                                onChange={(e) => setSchedule({ sendHour: Number(e.target.value) })}
                                            >
                                                {Array.from({ length: 24 }, (_v, h) => (
                                                    <MenuItem key={h} value={h}>
                                                        {hour(h)}
                                                    </MenuItem>
                                                ))}
                                            </TextField>
                                        </Grid>
                                    )}
                                    {fields.includes("repeatDaily") && (
                                        <Grid item xs={12}>
                                            <OptionTile
                                                checked={draft.schedule.repeatDaily !== false}
                                                label="Her gün tekrarla"
                                                hint="Durum sürdükçe hatırlatma saatinde yeniden gönderilir"
                                                onChange={(checked) => setSchedule({ repeatDaily: checked })}
                                            />
                                        </Grid>
                                    )}
                                </Grid>
                            </Section>
                        )}
                    </MDBox>
                </MDBox>
            </DialogContent>

            <DialogActions sx={{ px: 3, py: 1.75, gap: 1, borderTop: `1px solid ${UI.border}`, justifyContent: "space-between", flexWrap: "wrap" }}>
                <MDBox display="flex" alignItems="center" gap={0.75} minWidth={0} sx={{ flex: "1 1 280px" }}>
                    <InfoOutlinedIcon sx={{ color: UI.muted, fontSize: "16px !important", flexShrink: 0 }} />
                    <MDTypography variant="caption" sx={{ color: UI.muted, lineHeight: 1.4 }}>
                        {summary}
                    </MDTypography>
                </MDBox>
                <MDBox display="flex" gap={1}>
                    <Button
                        onClick={onClose}
                        variant="outlined"
                        sx={{ borderRadius: "10px", textTransform: "none", color: `${UI.text} !important`, borderColor: "#cbd5e1" }}
                    >
                        Vazgeç
                    </Button>
                    <Button variant="contained" onClick={apply} sx={{ borderRadius: "10px", textTransform: "none", px: 3, color: "#fff !important" }}>
                        Uygula
                    </Button>
                </MDBox>
            </DialogActions>
        </Dialog>
    );
}

RuleDialog.defaultProps = { row: null };
RuleDialog.propTypes = {
    row: PropTypes.object,
    meta: PropTypes.object.isRequired,
    users: PropTypes.arrayOf(PropTypes.object).isRequired,
    meId: PropTypes.string.isRequired,
    customerContacts: PropTypes.arrayOf(PropTypes.object).isRequired,
    onClose: PropTypes.func.isRequired,
    onApply: PropTypes.func.isRequired,
};

export default function NotificationRowsEditor({ rows, types, users, meId, customerContacts, onChange, disabled }) {
    const [editingType, setEditingType] = useState(null);
    const update = (type, patch) => onChange(rows.map((row) => (row.type === type ? { ...row, ...patch } : row)));
    const userById = new Map(users.map((u) => [String(u.id), u]));
    const editingRow = rows.find((r) => r.type === editingType) || null;

    const renderCustomer = (row, meta) => {
        if (meta.internalOnly) return <Pill tone="neutral">Yalnızca iç</Pill>;
        if (!row.notifyCustomer) return <MDTypography variant="caption" sx={{ color: UI.muted }}>Kapalı</MDTypography>;
        const count = (row.customerEmails || []).length;
        return (
            <MDBox display="flex" flexDirection="column" alignItems="flex-start" gap={0.25}>
                <Pill tone={row.customerMode === "AUTO" ? "success" : "warning"}>{row.customerMode === "AUTO" ? "Otomatik" : "Onaylı"}</Pill>
                <MDTypography variant="caption" sx={{ color: count ? UI.muted : UI.tone.danger.fg }}>
                    {count ? `${count} yetkili` : "Yetkili seçilmedi"}
                </MDTypography>
            </MDBox>
        );
    };

    return (
        <MDBox sx={{ containerType: "inline-size", textAlign: "left", whiteSpace: "normal" }}>
            {GROUPS.map((group) => {
                const groupRows = rows.filter((row) => (types[row.type]?.category || "EVENT") === group.category);
                if (!groupRows.length) return null;
                const activeCount = groupRows.filter((r) => r.enabled !== false).length;
                return (
                    <MDBox key={group.category} mb={3} sx={{ border: `1px solid ${UI.border}`, borderRadius: "12px", overflow: "hidden", backgroundColor: "#fff" }}>
                        <MDBox px={2} py={1.5} display="flex" justifyContent="space-between" alignItems="center" sx={{ backgroundColor: UI.subtle, borderBottom: `1px solid ${UI.border}` }}>
                            <MDBox>
                                <MDTypography variant="button" fontWeight="bold" sx={{ color: UI.text }}>
                                    {group.title}
                                </MDTypography>
                                <MDTypography variant="caption" display="block" sx={{ color: UI.muted }}>
                                    {group.subtitle}
                                </MDTypography>
                            </MDBox>
                            <MDTypography variant="caption" sx={{ color: UI.muted, whiteSpace: "nowrap" }}>
                                {activeCount} / {groupRows.length} aktif
                            </MDTypography>
                        </MDBox>

                        <MDBox
                            columnGap={2}
                            px={2}
                            py={1}
                            sx={{ ...ROW_GRID, borderBottom: `1px solid ${UI.border}`, "& > *": { fontSize: 11, fontWeight: 600, color: UI.muted, textTransform: "uppercase", letterSpacing: 0.4 } }}
                        >
                            <MDBox />
                            <MDBox>Bildirim</MDBox>
                            <MDBox sx={DESKTOP_ONLY}>İç alıcılar</MDBox>
                            <MDBox sx={DESKTOP_ONLY}>Kanal</MDBox>
                            <MDBox sx={DESKTOP_ONLY}>Müşteri</MDBox>
                            <MDBox sx={DESKTOP_ONLY}>Zamanlama</MDBox>
                            <MDBox />
                        </MDBox>

                        {groupRows.map((row, index) => {
                            const meta = types[row.type] || {};
                            const enabled = row.enabled !== false;
                            const names = internalNames(row, meId, userById);
                            const channels = row.channels || {};
                            return (
                                <MDBox
                                    key={row.type}
                                    columnGap={2}
                                    alignItems="center"
                                    px={2}
                                    py={1.25}
                                    sx={{
                                        ...ROW_GRID,
                                        borderTop: index ? `1px solid ${UI.border}` : "none",
                                        opacity: enabled ? 1 : 0.55,
                                        transition: "background-color .15s",
                                        "&:hover": { backgroundColor: UI.subtle },
                                    }}
                                >
                                    <Switch size="small" checked={enabled} disabled={disabled} onChange={(e) => update(row.type, { enabled: e.target.checked })} />
                                    <MDBox minWidth={0}>
                                        <MDBox display="flex" alignItems="center" gap={0.75} flexWrap="wrap">
                                            <MDTypography variant="button" fontWeight="medium" sx={{ color: UI.text }}>
                                                {meta.label}
                                            </MDTypography>
                                            {meta.priority === "HIGH" && <Pill tone="danger">Yüksek öncelik</Pill>}
                                            {row.overridden && <Pill tone="info">Dosyaya özel</Pill>}
                                        </MDBox>
                                        <MDTypography variant="caption" display="block" sx={{ color: UI.muted, lineHeight: 1.4 }}>
                                            {meta.description || TYPE_DESCRIPTIONS[row.type]}
                                        </MDTypography>
                                    </MDBox>
                                    <MDBox sx={DESKTOP_ONLY} minWidth={0}>
                                        <Tooltip title={names.join(", ")}>
                                            <MDTypography variant="caption" display="block" noWrap sx={{ color: names.length ? UI.text : UI.tone.danger.fg }}>
                                                {names.length ? names.slice(0, 2).join(", ") : "Alıcı yok"}
                                                {names.length > 2 ? ` +${names.length - 2}` : ""}
                                            </MDTypography>
                                        </Tooltip>
                                    </MDBox>
                                    <MDBox sx={DESKTOP_ONLY}>
                                        <MDBox display="flex" gap={0.75}>
                                            <ChannelToggle
                                                label="Sistem içi"
                                                active={channels.inApp !== false}
                                                disabled={disabled}
                                                icon={<NotificationsNoneOutlinedIcon sx={{ fontSize: 18 }} />}
                                                onClick={() => update(row.type, { channels: { ...channels, inApp: channels.inApp === false } })}
                                            />
                                            <ChannelToggle
                                                label="E-posta"
                                                active={channels.email !== false}
                                                disabled={disabled}
                                                icon={<MailOutlineIcon sx={{ fontSize: 18 }} />}
                                                onClick={() => update(row.type, { channels: { ...channels, email: channels.email === false } })}
                                            />
                                        </MDBox>
                                    </MDBox>
                                    <MDBox sx={DESKTOP_ONLY}>{renderCustomer(row, meta)}</MDBox>
                                    <MDBox sx={DESKTOP_ONLY}>
                                        <MDTypography variant="caption" display="block" sx={{ color: UI.text, lineHeight: 1.4 }}>
                                            {scheduleSummary(row)}
                                        </MDTypography>
                                    </MDBox>
                                    <Tooltip title="Kuralı düzenle">
                                        <span>
                                            <IconButton size="small" disabled={disabled} onClick={() => setEditingType(row.type)} aria-label="Kuralı düzenle">
                                                <EditOutlinedIcon fontSize="small" />
                                            </IconButton>
                                        </span>
                                    </Tooltip>
                                </MDBox>
                            );
                        })}
                    </MDBox>
                );
            })}

            <RuleDialog
                row={editingRow}
                meta={(editingRow && types[editingRow.type]) || {}}
                users={users}
                meId={meId || ""}
                customerContacts={customerContacts}
                onClose={() => setEditingType(null)}
                onApply={(next) => {
                    update(next.type, next);
                    setEditingType(null);
                }}
            />
        </MDBox>
    );
}

NotificationRowsEditor.defaultProps = {
    users: [],
    meId: "",
    customerContacts: [],
    disabled: false,
};

NotificationRowsEditor.propTypes = {
    rows: PropTypes.arrayOf(PropTypes.object).isRequired,
    types: PropTypes.object.isRequired,
    users: PropTypes.arrayOf(PropTypes.object),
    meId: PropTypes.string,
    customerContacts: PropTypes.arrayOf(PropTypes.object),
    onChange: PropTypes.func.isRequired,
    disabled: PropTypes.bool,
};
